
import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
        <div className="px-32 py-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="https://public.readdy.ai/ai/img_res/8af8c14c-d575-4864-a9d1-9a0ff80999cf.png" alt="PRODEL S.R.L." className="h-12 w-auto" />
            <h1 className="text-xl font-light tracking-widest text-gray-900">PRODEL S.R.L.</h1>
          </div>
          <ul className="flex items-center gap-16">
            <li><button onClick={() => scrollToSection('home')} className="text-base font-medium text-gray-700 hover:text-gray-900 hover:font-semibold transition-all cursor-pointer whitespace-nowrap">Home</button></li>
            <li><button onClick={() => scrollToSection('about')} className="text-base font-medium text-gray-700 hover:text-gray-900 hover:font-semibold transition-all cursor-pointer whitespace-nowrap">About</button></li>
            <li><button onClick={() => scrollToSection('products')} className="text-base font-medium text-gray-700 hover:text-gray-900 hover:font-semibold transition-all cursor-pointer whitespace-nowrap">Products</button></li>
            <li><button onClick={() => scrollToSection('features')} className="text-base font-medium text-gray-700 hover:text-gray-900 hover:font-semibold transition-all cursor-pointer whitespace-nowrap">Features</button></li>
            <li><button onClick={() => scrollToSection('contact')} className="text-base font-medium text-gray-700 hover:text-gray-900 hover:font-semibold transition-all cursor-pointer whitespace-nowrap">Contact</button></li>
          </ul>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-transparent"></div>
        
        <div className="relative z-10 w-full px-32 py-32">
          <div className="grid grid-cols-2 gap-24 items-center">
            <div>
              <div className="space-y-6">
                <h2 className="text-8xl font-bold text-gray-900 leading-tight">
                  Modern<br />
                  <span className="ml-10">Applications</span><br />
                  <span className="ml-20">for iOS</span>
                </h2>
                <p className="text-xl text-gray-600 leading-relaxed max-w-xl ml-20">
                  A team of experts developing cutting-edge applications for the iOS platform, designed to enhance user experience and improve mobile device security.
                </p>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="w-full h-96 rounded-3xl bg-gradient-to-br from-indigo-100 to-purple-100 shadow-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-40 px-32 bg-white">
        <div className="grid grid-cols-2 gap-24">
          <div className="bg-gray-800 rounded-5xl p-16 relative">
            <div className="absolute top-8 left-8 bg-orange-500 text-white text-xs font-semibold px-6 py-2 rounded-full uppercase tracking-wider">
              Expertise
            </div>
            <div className="mt-16">
              <h3 className="text-5xl font-semibold text-white mb-8 leading-tight">
                Team of iOS Development Experts
              </h3>
              <p className="text-gray-400 text-lg leading-relaxed">
                Our dedicated team specializes in creating innovative iOS applications that prioritize user experience, security, and performance. With years of expertise in mobile development, we deliver solutions that meet the highest standards.
              </p>
            </div>
          </div>

          <div className="space-y-12">
            <div>
              <h3 className="text-6xl font-serif text-gray-900 mb-8">Our Mission</h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-12">
                We are committed to developing modern, secure, and user-friendly applications that enhance the iOS ecosystem. Our products are designed with attention to detail, ensuring optimal performance and user satisfaction.
              </p>
            </div>
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img 
                src="https://public.readdy.ai/ai/img_res/315810aff752caf9db93c5e24fb4259e.jpg" 
                alt="Modern workspace" 
                className="w-full h-96 object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-40 px-32 bg-gray-50">
        <div className="text-center mb-32">
          <h2 className="text-7xl font-semibold text-gray-900 mb-6">Our Products</h2>
          <p className="text-xl text-gray-600">Innovative solutions for iOS users</p>
        </div>

        <div className="grid grid-cols-2 gap-12 max-w-7xl mx-auto" data-product-shop>
          {/* VPN Product */}
          <div className="bg-white rounded-5xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300">
            <div className="relative h-96 bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center">
              <div className="absolute top-8 left-8 bg-gray-900 text-white text-sm font-semibold px-6 py-3 rounded-full flex items-center gap-2">
                <i className="ri-shield-check-line text-lg"></i>
                <span>SECURITY</span>
              </div>
              <div className="w-32 h-32 flex items-center justify-center">
                <i className="ri-shield-keyhole-line text-9xl text-white"></i>
              </div>
            </div>
            <div className="p-12 text-center bg-white">
              <h3 className="text-4xl font-bold text-gray-900 mb-6">VPN</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Secure your online presence with our advanced VPN solution. Protect your privacy, encrypt your data, and browse safely on any network.
              </p>
            </div>
          </div>

          {/* Cleaner Product */}
          <div className="bg-white rounded-5xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300">
            <div className="relative h-96 bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <div className="absolute top-8 left-8 bg-gray-900 text-white text-sm font-semibold px-6 py-3 rounded-full flex items-center gap-2">
                <i className="ri-speed-up-line text-lg"></i>
                <span>PERFORMANCE</span>
              </div>
              <div className="w-32 h-32 flex items-center justify-center">
                <i className="ri-delete-bin-line text-9xl text-white"></i>
              </div>
            </div>
            <div className="p-12 text-center bg-white">
              <h3 className="text-4xl font-bold text-gray-900 mb-6">Cleaner</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Optimize your device performance with our intelligent cleaner. Remove unnecessary files, free up storage, and keep your iOS device running smoothly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-40 px-32 bg-amber-50">
        <div className="relative">
          <div className="absolute top-0 left-0 text-gray-500 text-sm font-light tracking-widest">
            <div>PRODEL</div>
          </div>

          <div className="text-center mb-24 pt-16">
            <h2 className="text-8xl font-serif text-gray-900 mb-6">Platform Features</h2>
          </div>

          <div className="grid grid-cols-3 gap-12 max-w-7xl mx-auto">
            {/* Security First */}
            <div className="bg-gray-900 rounded-5xl p-12 relative">
              <div className="w-16 h-16 rounded-full bg-gray-700 flex items-center justify-center mb-8">
                <span className="text-white text-2xl font-bold">S</span>
              </div>
              <h3 className="text-4xl font-semibold text-white mb-6 leading-tight">Security First</h3>
              <p className="text-gray-400 text-base leading-relaxed">
                Built with industry-leading security protocols to protect user data and ensure privacy at every level.
              </p>
            </div>

            {/* Optimized Experience */}
            <div className="relative rounded-5xl overflow-hidden">
              <img 
                src="https://readdy.ai/api/search-image?query=abstract%20modern%20technology%20interface%20holographic%20display%20futuristic%20digital%20elements%20clean%20minimalist%20design%20blue%20purple%20gradient%20background%20high%20tech%20visualization&width=600&height=800&seq=features-performance-001&orientation=portrait" 
                alt="Performance" 
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-transparent"></div>
              <div className="absolute top-8 left-8 bg-white/20 backdrop-blur-sm text-white text-sm font-semibold px-6 py-3 rounded-full flex items-center gap-2">
                <i className="ri-flashlight-line text-lg"></i>
                <span>PERFORMANCE</span>
              </div>
              <div className="absolute bottom-12 left-8 right-8">
                <h3 className="text-5xl font-semibold text-white leading-tight">Optimized Experience</h3>
              </div>
            </div>

            {/* User Privacy */}
            <div className="space-y-8">
              <div>
                <h3 className="text-5xl font-serif text-gray-900 mb-6 leading-tight">User Privacy</h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  Your data belongs to you. We implement strict privacy measures to ensure complete control over personal information.
                </p>
              </div>
              <div className="rounded-3xl overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=smartphone%20mobile%20device%20with%20security%20lock%20icon%20privacy%20protection%20concept%20clean%20modern%20interface%20minimal%20design%20soft%20lighting%20professional%20technology%20photography&width=600&height=400&seq=features-privacy-001&orientation=landscape" 
                  alt="Privacy" 
                  className="w-full h-64 object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-gray-950 text-white py-32 px-32">
        <div className="mb-24">
          <h2 className="text-9xl font-extrabold mb-4">PRODEL S.R.L.</h2>
        </div>

        <div className="grid grid-cols-3 gap-20">
          {/* Company Info */}
          <div className="col-span-1">
            <p className="text-lg leading-relaxed text-gray-300 mb-16 pr-8">
              We are committed to delivering exceptional iOS applications that prioritize security, performance, and user experience. Get in touch with us to learn more about our products and services.
            </p>
            <div className="flex gap-6">
              <div className="w-12 h-12 rounded-full border-2 border-white flex items-center justify-center hover:bg-white hover:text-gray-950 transition-all cursor-pointer">
                <i className="ri-facebook-fill text-xl"></i>
              </div>
              <div className="w-12 h-12 rounded-full border-2 border-white flex items-center justify-center hover:bg-white hover:text-gray-950 transition-all cursor-pointer">
                <i className="ri-twitter-x-line text-xl"></i>
              </div>
              <div className="w-12 h-12 rounded-full border-2 border-white flex items-center justify-center hover:bg-white hover:text-gray-950 transition-all cursor-pointer">
                <i className="ri-linkedin-fill text-xl"></i>
              </div>
              <div className="w-12 h-12 rounded-full border-2 border-white flex items-center justify-center hover:bg-white hover:text-gray-950 transition-all cursor-pointer">
                <i className="ri-instagram-line text-xl"></i>
              </div>
            </div>
          </div>

          {/* Contact Details */}
          <div className="col-span-1 space-y-16">
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Address</h4>
              <p className="text-lg text-white leading-relaxed">
                Avenida Doctor Amadeo Sabattini 2373<br />
                Río Cuarto, X5802DNE
              </p>
            </div>

            <div>
              <h4 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Country</h4>
              <p className="text-lg text-white">Argentina</p>
            </div>

            <div>
              <h4 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Email</h4>
              <p className="text-lg text-white mb-10">help@prodelsrl.com</p>
              
              <h4 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Phone</h4>
              <p className="text-lg text-white">+1 2792071774</p>
            </div>
          </div>

          {/* Image */}
          <div className="col-span-1">
            <div className="rounded-3xl overflow-hidden h-full">
              <img 
                src="https://readdy.ai/api/search-image?query=modern%20office%20building%20exterior%20contemporary%20architecture%20glass%20facade%20professional%20business%20environment%20clean%20lines%20urban%20setting%20daytime%20natural%20lighting%20high%20quality%20photography&width=600&height=800&seq=contact-office-001&orientation=portrait" 
                alt="Office" 
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-24 pt-16 border-t border-gray-800">
          <div className="flex justify-between items-center">
            <p className="text-base text-gray-500">© 2025 PRODEL S.R.L.</p>
            <div className="flex gap-10">
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-base text-gray-500 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
